// src/components/Customers/CustomerDetailsShadcn.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Edit,
  Trash2,
  Plus,
  FileText,
  Clock,
  Eye
} from 'lucide-react';

// Import shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

// Import custom components
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hooks
import { useCustomers } from '../../hooks/useCustomers';

const CustomerDetailsShadcn = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  // Get customer data
  const {
    selectedCustomer,
    selectedCustomerLoading,
    selectedCustomerError,
    loadCustomerDetails,
    updateCustomer,
    deleteCustomer,
    clearSelectedCustomer,
    isConnected
  } = useCustomers();

  // Local state
  const [activeTab, setActiveTab] = useState('appointments');

  // Load customer details on mount
  useEffect(() => {
    if (isConnected && customerId && !selectedCustomer) {
      console.log('Loading customer details for:', customerId);
      loadCustomerDetails(customerId, {
        includeAppointments: true,
        includeInvoices: true,
        includeStatistics: true
      });
    }
  }, [isConnected, customerId, selectedCustomer, loadCustomerDetails]);

  // Clear selected customer when component unmounts
  useEffect(() => {
    return () => {
      clearSelectedCustomer();
    };
  }, [clearSelectedCustomer]);

  // Re-load if customerId changes
  useEffect(() => {
    if (customerId && selectedCustomer?.customer?.id !== customerId) {
      console.log('Customer ID changed, loading new details:', customerId);
      loadCustomerDetails(customerId, {
        includeAppointments: true,
        includeInvoices: true,
        includeStatistics: true
      });
    }
  }, [customerId, selectedCustomer, loadCustomerDetails]);

  // Extract data from backend response
  const customer = React.useMemo(() => {
    if (!selectedCustomer) return null;

    if (selectedCustomer.customer) {
      return selectedCustomer.customer;
    } else if (selectedCustomer.id || selectedCustomer.customer_id) {
      return selectedCustomer;
    }

    return null;
  }, [selectedCustomer]);

  const appointments = React.useMemo(() => {
    if (!selectedCustomer) return [];
    return selectedCustomer.appointments ||
           selectedCustomer.appointment_history ||
           selectedCustomer.events ||
           [];
  }, [selectedCustomer]);

  const invoices = React.useMemo(() => {
    if (!selectedCustomer) return [];
    return selectedCustomer.invoices ||
           selectedCustomer.invoice_history ||
           selectedCustomer.billing_history ||
           [];
  }, [selectedCustomer]);

  // Calculate customer metrics
  const customerMetrics = React.useMemo(() => {
    if (!customer || !invoices) {
      return { totalSpent: 0, appointmentCount: 0, lastAppointment: null };
    }

    const totalSpent = invoices.reduce((sum, invoice) => {
      const amount = invoice.total || invoice.amount || invoice.total_amount || invoice.grand_total || 0;
      return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
    }, 0);

    const appointmentCount = appointments.length;

    const lastAppointment = appointments
      .filter(apt => {
        const status = apt.status || apt.appointment_status;
        return status === 'completed' || status === 'finished' || status === 'done';
      })
      .sort((a, b) => {
        const dateA = new Date(a.appointment_date || a.date || a.scheduled_date);
        const dateB = new Date(b.appointment_date || b.date || b.scheduled_date);
        return dateB - dateA;
      })[0] || null;

    return { totalSpent, appointmentCount, lastAppointment };
  }, [customer, appointments, invoices]);

  // Helper functions
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'completed':
      case 'paid':
        return 'default';
      case 'scheduled':
      case 'sent':
        return 'secondary';
      case 'cancelled':
      case 'overdue':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Event handlers
  const handleBack = () => {
    navigate('/customers');
  };

  const handleEditCustomer = () => {
    const customerIdToEdit = customer?.id || customer?.customer_id || customer?._id;
    console.log('Edit customer:', customerIdToEdit);
    // TODO: Implement edit functionality
  };

  const handleDeleteCustomer = () => {
    if (!customer) return;

    const customerIdToDelete = customer.id || customer.customer_id || customer._id;
    const customerName = customer.name || customer.full_name || customer.customer_name || 'this customer';

    if (window.confirm(`Are you sure you want to delete ${customerName}? This action cannot be undone.`)) {
      console.log('Deleting customer:', customerIdToDelete);
      deleteCustomer(customerIdToDelete);
      navigate('/customers');
    }
  };

  const handleCreateInvoice = () => {
    const customerName = customer?.name || customer?.full_name || customer?.customer_name;
    console.log('Create invoice for customer:', customerName);
    // TODO: Navigate to invoice creation
    // navigate('/invoices/new', { state: { customerId, customerName } });
  };

  const handleScheduleAppointment = () => {
    const customerName = customer?.name || customer?.full_name || customer?.customer_name;
    console.log('Schedule appointment for customer:', customerName);
    // TODO: Navigate to calendar
    // navigate('/calendar', { state: { preSelectedCustomer: customer } });
  };

  const handleViewAppointment = (appointment) => {
    const appointmentId = appointment.id || appointment.appointment_id || appointment._id;
    console.log('View appointment:', appointmentId);
    navigate(`/appointments/${appointmentId}`);
  };

  const handleViewInvoice = (invoice) => {
    const invoiceId = invoice.id || invoice.invoice_id || invoice._id;
    console.log('View invoice:', invoiceId);
    // TODO: Navigate when invoice detail page exists
    // navigate(`/invoices/${invoiceId}`);
  };

  // Connection status warning
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Connection Required</h2>
          <p className="text-muted-foreground mb-6">
            Please check your internet connection and try again.
          </p>
          <Button onClick={handleBack}>
            Back to Customers
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Loading state
  if (selectedCustomerLoading && !selectedCustomer) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading customer details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (selectedCustomerError && !selectedCustomer) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{selectedCustomerError}</AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Unable to Load Customer</h2>
          <p className="text-muted-foreground mb-6">{selectedCustomerError}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => loadCustomerDetails(customerId, {
              includeAppointments: true,
              includeInvoices: true,
              includeStatistics: true
            })}>
              Try Again
            </Button>
            <Button variant="outline" onClick={handleBack}>
              Back to Customers
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Customer not found
  if (!selectedCustomerLoading && !customer) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Customer Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The customer you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBack}>
            Back to Customers
          </Button>
        </div>
      </PageContainer>
    );
  }

  const customerName = customer?.name || customer?.full_name || customer?.customer_name || 'Unknown Customer';
  const customerEmail = customer?.email || customer?.email_address || '';
  const customerPhone = customer?.phone || customer?.phone_number || customer?.mobile || '';
  const customerAddress = customer?.address || '';

  return (
    <PageContainer>
      {/* Back Button & Header */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Customers
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleEditCustomer}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={handleDeleteCustomer}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {selectedCustomerError && customer && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{selectedCustomerError}</AlertDescription>
        </Alert>
      )}

      {/* Customer Profile Card */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="bg-gradient-to-br from-emerald-500/20 to-blue-500/20 text-emerald-400 text-2xl">
                {getInitials(customerName)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{customerName}</h1>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {customerEmail && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span>{customerEmail}</span>
                  </div>
                )}
                {customerPhone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <span>{customerPhone}</span>
                  </div>
                )}
                {customerAddress && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{customerAddress}</span>
                  </div>
                )}
              </div>

              <Separator className="my-4" />

              {/* Customer Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-bold">{customerMetrics.appointmentCount}</div>
                  <div className="text-sm text-muted-foreground">Appointments</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{formatCurrency(customerMetrics.totalSpent)}</div>
                  <div className="text-sm text-muted-foreground">Total Spent</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {customerMetrics.lastAppointment ? formatDate(customerMetrics.lastAppointment.appointment_date) : 'Never'}
                  </div>
                  <div className="text-sm text-muted-foreground">Last Visit</div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={handleScheduleAppointment}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Appointment
                </Button>
                <Button variant="outline" onClick={handleCreateInvoice}>
                  <FileText className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Appointments and Invoices */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="appointments">
            <Calendar className="w-4 h-4 mr-2" />
            Appointments ({appointments.length})
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <FileText className="w-4 h-4 mr-2" />
            Invoices ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* Appointments Tab */}
        <TabsContent value="appointments">
          <Card>
            <CardHeader>
              <CardTitle>Appointment History</CardTitle>
              <CardDescription>All appointments for this customer</CardDescription>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                  <p className="text-muted-foreground mb-4">No appointments yet</p>
                  <Button onClick={handleScheduleAppointment}>
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Appointment
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((appointment) => (
                        <TableRow key={appointment.id || appointment.appointment_id}>
                          <TableCell>
                            {formatDate(appointment.scheduled_datetime || appointment.appointment_date || appointment.date)}
                          </TableCell>
                          <TableCell>{appointment.service_type || appointment.title || 'Service'}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadge(appointment.status)}>
                              {appointment.status || 'pending'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAppointment(appointment)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <CardTitle>Invoice History</CardTitle>
              <CardDescription>All invoices for this customer</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                  <p className="text-muted-foreground mb-4">No invoices yet</p>
                  <Button onClick={handleCreateInvoice}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Invoice
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id || invoice.invoice_id}>
                          <TableCell>#{invoice.invoice_number || invoice.id}</TableCell>
                          <TableCell>{formatDate(invoice.invoice_date || invoice.created_at || invoice.date)}</TableCell>
                          <TableCell>{formatCurrency(invoice.total || invoice.amount || invoice.total_amount)}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadge(invoice.status)}>
                              {invoice.status || 'draft'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewInvoice(invoice)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
};

export default CustomerDetailsShadcn;
